import { forwardRef, type HTMLAttributes } from 'react';

interface SkeletonProps extends HTMLAttributes<HTMLDivElement> {
  /** バリアント（default: シマー, glass: ガラスモーフィズム） */
  variant?: 'default' | 'glass';
  /** 幅 */
  width?: string | number;
  /** 高さ */
  height?: string | number;
  /** 角丸 */
  rounded?: 'sm' | 'md' | 'lg' | 'full';
}

interface SkeletonTextProps {
  /** 行数 */
  lines?: number;
  /** 最後の行の幅 */
  lastLineWidth?: string;
  /** バリアント */
  variant?: 'default' | 'glass';
}

interface SkeletonCircleProps extends HTMLAttributes<HTMLDivElement> {
  /** サイズ（直径） */
  size?: string | number;
  /** バリアント */
  variant?: 'default' | 'glass';
}

/** @graph-connects none */
const variantStyles = {
  default: 'skeleton-shimmer',
  glass: 'skeleton-glass',
};

/** @graph-connects none */
const roundedStyles = {
  sm: 'rounded-sm',
  md: 'rounded-md',
  lg: 'rounded-lg',
  full: 'rounded-full',
};

/**
 * @graph-stack cortex-admin
 * @graph-domain Engineering
 * @graph-business スケルトンローディング。default（シマー）とglass（ガラスモーフィズム）の2バリアントで読み込み中プレースホルダーを表示
 * @graph-connects none
 */
export const Skeleton = forwardRef<HTMLDivElement, SkeletonProps>(
  (
    { variant = 'default', width, height, rounded = 'md', className = '', style, ...props },
    ref,
  ) => {
    return (
      <div
        ref={ref}
        className={`
          ${variantStyles[variant]}
          ${roundedStyles[rounded]}
          ${className}
        `}
        style={{
          width: width ?? '100%',
          height: height ?? '1rem',
          ...style,
        }}
        aria-hidden="true"
        {...props}
      />
    );
  },
);

Skeleton.displayName = 'Skeleton';

/**
 * @graph-stack cortex-admin
 * @graph-domain Engineering
 * @graph-business 複数行テキストスケルトン。指定行数のスケルトン行を生成し、最終行は短縮表示
 * @graph-connects none
 */
export function SkeletonText({
  lines = 3,
  lastLineWidth = '60%',
  variant = 'default',
}: SkeletonTextProps) {
  return (
    <div className="space-y-2">
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton
          key={i}
          variant={variant}
          width={i === lines - 1 ? lastLineWidth : '100%'}
          height="1rem"
        />
      ))}
    </div>
  );
}

/**
 * @graph-stack cortex-admin
 * @graph-domain Engineering
 * @graph-business 円形スケルトン。アバター等の読み込み中プレースホルダー
 * @graph-connects none
 */
export const SkeletonCircle = forwardRef<HTMLDivElement, SkeletonCircleProps>(
  ({ size = 40, variant = 'default', className = '', style, ...props }, ref) => {
    const sizeValue = typeof size === 'number' ? String(size) + 'px' : size;
    return (
      <div
        ref={ref}
        className={`
          ${variantStyles[variant]}
          rounded-full
          ${className}
        `}
        style={{
          width: sizeValue,
          height: sizeValue,
          ...style,
        }}
        aria-hidden="true"
        {...props}
      />
    );
  },
);

SkeletonCircle.displayName = 'SkeletonCircle';
